// src/components/analytics/predictors/ExponentialSmoothingPredictor.js

import { BasePredictor } from './base/BasePredictor';
import { 
  parseDate, 
  formatDate, 
  analyzeDateFrequency,
  generateFutureDatesWithFrequency,
  detectDateFormat
} from '../utils/dateUtils';

export class ExponentialSmoothingPredictor extends BasePredictor {
  constructor(config) {
    super(config);
    this.method = 'exponential_smoothing';
    
    // Smoothing parameters with defaults
    this.alpha = (config.alpha !== undefined) ? config.alpha : 0.6;   // faster level response
    this.beta = (config.beta !== undefined) ? config.beta : 0.2;     // faster trend response
    this.gamma = (config.gamma !== undefined) ? config.gamma : 0.15; // slightly stronger seasonality
    
    // Method selection
    this.smoothingMethod = config.smoothingMethod || 'auto'; // 'simple', 'double', 'triple', 'auto'
    
    // Seasonal configuration
    this.seasonalPeriod = config.seasonalPeriod || null;
    this.seasonalType = config.seasonalType || 'additive'; // 'additive' or 'multiplicative'
    
    // Trend damping
    this.dampingFactor = config.dampingFactor || 0.95; // Default 95% retention per period
    
    console.log('Exponential Smoothing initialized with:', {
      alpha: this.alpha,
      beta: this.beta,
      gamma: this.gamma,
      method: this.smoothingMethod,
      seasonalPeriod: this.seasonalPeriod,
      dampingFactor: this.dampingFactor
    });
  }

  /**
   * Calculate coefficient of variation to measure data volatility
   */
  calculateCoefficientOfVariation(values) {
    if (values.length < 2) return 0;
    
    const mean = values.reduce((a, b) => a + b, 0) / values.length;
    if (mean === 0) return 0;
    
    const variance = values.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / values.length;
    const stdDev = Math.sqrt(variance);
    
    return stdDev / Math.abs(mean);
  }

  /**
   * Calculate initial trend using linear regression
   */
  calculateInitialTrend(values) {
    if (values.length < 2) return 0;
    
    // Use first 3-5 points for trend estimation
    const pointsForTrend = Math.min(5, values.length);
    let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
    
    for (let i = 0; i < pointsForTrend; i++) {
      sumX += i;
      sumY += values[i];
      sumXY += i * values[i];
      sumX2 += i * i;
    }
    
    const n = pointsForTrend;
    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    
    // Check for volatility and dampen trend if needed
    const cv = this.calculateCoefficientOfVariation(values.slice(0, pointsForTrend));
    if (cv > 0.3) { // High volatility
      return slope * (1 - cv); // Dampen proportionally to volatility
    }
    
    return slope;
  }

  /**
   * Simple Exponential Smoothing (SES)
   * For data with no trend or seasonality
   */
  simpleExponentialSmoothing(values, numPredictions) {
    if (values.length === 0) throw new Error('No data for smoothing');
    
    const smoothed = [values[0]]; // Initialize with first value
    
    // Smooth historical data
    for (let i = 1; i < values.length; i++) {
      const forecast = this.alpha * values[i] + (1 - this.alpha) * smoothed[i - 1];
      smoothed.push(forecast);
    }
    
    // Generate flat forecasts (characteristic of SES)
    const lastSmoothed = smoothed[smoothed.length - 1];
    const forecasts = Array(numPredictions).fill(lastSmoothed);
    
    return {
      smoothed,
      forecasts,
      level: lastSmoothed
    };
  }

  /**
   * Double Exponential Smoothing (Holt's Method)
   * For data with trend but no seasonality
   */
  doubleExponentialSmoothing(values, numPredictions) {
    if (values.length < 2) throw new Error('Need at least 2 points for double smoothing');
    
    // Initialize with better trend estimation
    const level = [values[0]];
    const initialTrend = this.calculateInitialTrend(values);
    const trend = [initialTrend];
    const smoothed = [values[0]];
    
    // Smooth historical data
    for (let i = 1; i < values.length; i++) {
      // Update level
      const newLevel = this.alpha * values[i] + (1 - this.alpha) * (level[i - 1] + trend[i - 1]);
      level.push(newLevel);
      
      // Update trend
      const newTrend = this.beta * (newLevel - level[i - 1]) + (1 - this.beta) * trend[i - 1];
      trend.push(newTrend);
      
      // One-step-ahead forecast
      smoothed.push(newLevel + newTrend);
    }
    
    // Generate forecasts with damped trend
    const lastLevel = level[level.length - 1];
    const lastTrend = trend[trend.length - 1];
    const forecasts = [];
    
    // Determine if damping should be applied
    const cv = this.calculateCoefficientOfVariation(values);
    const shouldDamp = cv > 0.4 || numPredictions > 18; // Damp for volatile data or long horizons
    
    for (let h = 1; h <= numPredictions; h++) {
      // Apply trend damping for long-term forecasts
      const dampedTrend = shouldDamp 
        ? lastTrend * Math.pow(this.dampingFactor, h - 1)
        : lastTrend;
      
      forecasts.push(lastLevel + h * dampedTrend);
    }
    
    // Ensure non-negative forecasts if historical data is non-negative
    const minHist = Math.min(...values);
    if (minHist >= 0) {
      for (let i = 0; i < forecasts.length; i++) {
        forecasts[i] = Math.max(0, forecasts[i]);
      }
    }

    return {
      smoothed,
      forecasts,
      level: lastLevel,
      trend: lastTrend,
      damped: shouldDamp
    };
  }

  /**
   * Triple Exponential Smoothing (Holt-Winters Method)
   * For data with trend and seasonality
   */
  tripleExponentialSmoothing(values, numPredictions, seasonalPeriod) {
    if (values.length < seasonalPeriod * 2) {
      throw new Error(`Need at least ${seasonalPeriod * 2} points for seasonal smoothing`);
    }
    
    // Initialize components
    const level = [];
    const trend = [];
    const seasonal = [];
    const smoothed = [];
    
    // Initialize seasonal factors (average method)
    for (let i = 0; i < seasonalPeriod; i++) {
      let sum = 0;
      let count = 0;
      for (let j = i; j < values.length; j += seasonalPeriod) {
        sum += values[j];
        count++;
      }
      const avgForPosition = sum / count;
      const overallAvg = values.slice(0, seasonalPeriod).reduce((a, b) => a + b, 0) / seasonalPeriod;
      
      if (this.seasonalType === 'multiplicative') {
        seasonal[i] = avgForPosition / overallAvg || 1;
      } else {
        seasonal[i] = avgForPosition - overallAvg || 0;
      }
    }
    
    // Initialize level and trend
    level[0] = values.slice(0, seasonalPeriod).reduce((a, b) => a + b, 0) / seasonalPeriod;
    trend[0] = this.calculateInitialTrend(values);
    
    // Apply Holt-Winters
    for (let i = 0; i < values.length; i++) {
      const seasonIdx = i % seasonalPeriod;
      
      if (i === 0) {
        smoothed[i] = values[i];
        continue;
      }
      
      // Update level
      let newLevel;
      if (this.seasonalType === 'multiplicative') {
        newLevel = this.alpha * (values[i] / seasonal[seasonIdx]) + 
                  (1 - this.alpha) * (level[i - 1] + trend[i - 1]);
      } else {
        newLevel = this.alpha * (values[i] - seasonal[seasonIdx]) + 
                  (1 - this.alpha) * (level[i - 1] + trend[i - 1]);
      }
      level[i] = newLevel;
      
      // Update trend
      const newTrend = this.beta * (newLevel - level[i - 1]) + (1 - this.beta) * trend[i - 1];
      trend[i] = newTrend;
      
      // Update seasonal
      if (this.seasonalType === 'multiplicative') {
        seasonal[seasonIdx] = this.gamma * (values[i] / newLevel) + 
                             (1 - this.gamma) * seasonal[seasonIdx];
      } else {
        seasonal[seasonIdx] = this.gamma * (values[i] - newLevel) + 
                             (1 - this.gamma) * seasonal[seasonIdx];
      }
      
      // Calculate smoothed value
      if (this.seasonalType === 'multiplicative') {
        smoothed[i] = (level[i] + trend[i]) * seasonal[seasonIdx];
      } else {
        smoothed[i] = level[i] + trend[i] + seasonal[seasonIdx];
      }
    }
    
    // Generate forecasts
    const lastLevel = level[level.length - 1];
    const lastTrend = trend[trend.length - 1];
    const forecasts = [];
    
    // Determine if damping should be applied
    const cv = this.calculateCoefficientOfVariation(values);
    const shouldDamp = cv > 0.2 || numPredictions > seasonalPeriod * 2;
    
    for (let h = 1; h <= numPredictions; h++) {
      const seasonIdx = (values.length + h - 1) % seasonalPeriod;
      
      // Apply trend damping for long-term forecasts
      const dampedTrend = shouldDamp 
        ? lastTrend * Math.pow(this.dampingFactor, h - 1)
        : lastTrend;
      
      let forecast;
      if (this.seasonalType === 'multiplicative') {
        forecast = (lastLevel + h * dampedTrend) * seasonal[seasonIdx];
      } else {
        forecast = lastLevel + h * dampedTrend + seasonal[seasonIdx];
      }
      
      forecasts.push(forecast);
    }
    
    return {
      smoothed,
      forecasts,
      level: lastLevel,
      trend: lastTrend,
      seasonal: seasonal.slice(-seasonalPeriod),
      damped: shouldDamp
    };
  }

  /**
   * Detect seasonality in the data
   */
  detectSeasonality(values) {
    if (this.seasonalPeriod) return this.seasonalPeriod;
    
    if (values.length < 14) return 0;
    
    // Check common seasonal periods
    const periods = [4, 5, 7, 12, 52];
    let bestPeriod = 0;
    let bestScore = 0;
    
    for (const period of periods) {
      if (values.length < period * 2) continue;
      
      // Simple autocorrelation check
      let score = 0;
      let count = 0;
      
      for (let i = period; i < values.length; i++) {
        score += Math.abs(values[i] - values[i - period]);
        count++;
      }
      
      if (count > 0) {
        const avgDiff = score / count;
        const avgValue = values.reduce((a, b) => a + b, 0) / values.length;
        const normalizedScore = 1 - (avgDiff / (avgValue + 0.01));
        
        if (normalizedScore > bestScore && normalizedScore > 0.3) {
          bestScore = normalizedScore;
          bestPeriod = period;
        }
      }
    }
    
    return bestPeriod;
  }

  /**
   * Automatically select the best smoothing method
   */
  selectBestMethod(values, seasonalPeriod) {
    // Need sufficient data
    if (values.length < 4) return 'simple';
    
    // Calculate volatility
    const cv = this.calculateCoefficientOfVariation(values);
    
    // Calculate trend strength
    let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
    const n = values.length;
    
    for (let i = 0; i < n; i++) {
      sumX += i;
      sumY += values[i];
      sumXY += i * values[i];
      sumX2 += i * i;
    }
    
    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const avgValue = sumY / n;
    const trendStrength = Math.abs(slope) / (avgValue || 1);
    
    // Check for consistency of trend
    const r2 = this.calculateR2(values);
    
    console.log(`Method selection: CV=${cv.toFixed(2)}, TrendStrength=${trendStrength.toFixed(3)}, R²=${r2.toFixed(3)}, DataPoints=${n}`);
    
    // For very high volatility (CV > 0.8), use simple only if no trend at all
    if (cv > 0.8 && trendStrength < 0.02) {
      console.log(`Very high volatility with no trend, using simple smoothing`);
      return 'simple';
    }
    
    // For short time series (< 12 points), prefer double over simple if any trend exists
    if (n < 12 && trendStrength > 0.01) {
      console.log(`Short series with some trend, using double smoothing`);
      return 'double';
    }
    
    // Check seasonality
    const hasSeasonality = seasonalPeriod > 0 && values.length >= seasonalPeriod * 2;
    
    // For moderate volatility, still use trend methods if trend is present
    if (cv <= 0.8 || (cv <= 1.0 && trendStrength > 0.05)) {
      if (hasSeasonality) return 'triple';
      if (trendStrength > 0.02 || r2 > 0.2) return 'double';
    }
    
    // Default to simple for extreme cases
    return 'simple';
  }

  /**
   * Calculate R-squared for trend consistency
   */
  calculateR2(values) {
    const n = values.length;
    if (n < 3) return 0;
    
    // Calculate linear regression
    let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
    
    for (let i = 0; i < n; i++) {
      sumX += i;
      sumY += values[i];
      sumXY += i * values[i];
      sumX2 += i * i;
    }
    
    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;
    
    // Calculate R-squared
    const meanY = sumY / n;
    let ssTotal = 0, ssResidual = 0;
    
    for (let i = 0; i < n; i++) {
      const predicted = intercept + slope * i;
      ssTotal += Math.pow(values[i] - meanY, 2);
      ssResidual += Math.pow(values[i] - predicted, 2);
    }
    
    return ssTotal === 0 ? 0 : 1 - (ssResidual / ssTotal);
  }

  /**
   * Calculate prediction intervals
   */
  calculatePredictionIntervals(values, forecasts, smoothed, confidenceLevel = 0.95) {
    // Calculate residuals from smoothed values
    const residuals = [];
    for (let i = 1; i < values.length && i < smoothed.length; i++) {
      residuals.push(values[i] - smoothed[i]);
    }
    
    if (residuals.length === 0) {
      return forecasts.map(() => ({ lower: 0, upper: 0 }));
    }
    
    // Calculate standard deviation of residuals
    const mean = residuals.reduce((a, b) => a + b, 0) / residuals.length;
    const variance = residuals.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / residuals.length;
    const stdDev = Math.sqrt(variance);
    
    // Z-score for confidence level
    const zScore = confidenceLevel === 0.95 ? 1.96 : 2.58;
    
    // Calculate intervals with growing uncertainty
    return forecasts.map((forecast, h) => {
      const uncertainty = stdDev * Math.sqrt(1 + h * 0.1) * zScore;
      return {
        lower: forecast - uncertainty,
        upper: forecast + uncertainty
      };
    });
  }

  /**
   * Main prediction method
   */
  predict(data, xAxis, yAxis) {
    // Validate data
    const validation = this.validateData(data, xAxis, yAxis);
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    // Extract and prepare time series
    const timeSeries = this.extractTimeSeries(
      data, 
      xAxis, 
      yAxis, 
      this.config.aggregationMethod || 'sum'
    );

    if (timeSeries.length < 2) {
      throw new Error('Need at least 2 data points for exponential smoothing');
    }

    // Check if x-axis is dates
    const firstX = timeSeries[0].x;
    const isDateAxis = this.looksLikeTimeSeries(firstX);
    
    if (!isDateAxis) {
      throw new Error('Exponential smoothing requires time series data with date/time x-axis');
    }

    // Parse dates and values
    const dates = [];
    const values = [];
    
    for (const point of timeSeries) {
      const parsedDate = parseDate(point.x);
      if (parsedDate) {
        dates.push(parsedDate);
        values.push(point.y);
      }
    }

    // Analyze frequency
    const dateFormat = detectDateFormat(timeSeries[0].x);
    const frequencyAnalysis = analyzeDateFrequency(dates);
    const { frequency, averageInterval } = frequencyAnalysis;

    // Detect seasonality
    const seasonalPeriod = this.detectSeasonality(values);
    
    // Select method
    let method = this.smoothingMethod;
    if (method === 'auto') {
      method = this.selectBestMethod(values, seasonalPeriod);
      console.log(`Auto-selected ${method} exponential smoothing`);
    }

    // === parameter grid search (quick SSE) ===
    if (this.config.autoTune !== false) {
      const tuned = this.tuneParameters(values, method, seasonalPeriod);
      this.alpha = tuned.alpha; this.beta = tuned.beta; this.gamma = tuned.gamma;
      console.log(`ES auto-tune → α=${this.alpha.toFixed(2)}, β=${this.beta.toFixed(2)}, γ=${this.gamma.toFixed(2)}`);
    }

    // Calculate predictions needed
    const lastDate = dates[dates.length - 1];
    const numPredictions = this.calculatePredictionsNeeded(
      lastDate,
      this.config.predictUntil,
      frequency,
      averageInterval
    );

    // Apply appropriate smoothing method
    let result;
    try {
      switch (method) {
        case 'simple':
          result = this.simpleExponentialSmoothing(values, numPredictions);
          break;
        case 'double':
          result = this.doubleExponentialSmoothing(values, numPredictions);
          break;
        case 'triple':
          if (seasonalPeriod > 0) {
            result = this.tripleExponentialSmoothing(values, numPredictions, seasonalPeriod);
          } else {
            console.warn('Triple smoothing requested but no seasonality detected, using double');
            result = this.doubleExponentialSmoothing(values, numPredictions);
            method = 'double';
          }
          break;
        default:
          throw new Error(`Unknown smoothing method: ${method}`);
      }
    } catch (error) {
      console.error('Smoothing error:', error);
      throw new Error(`Exponential smoothing failed: ${error.message}`);
    }

    // Generate future dates
    const futureDates = generateFutureDatesWithFrequency(
      lastDate,
      this.config.predictUntil,
      frequency,
      averageInterval
    ).slice(0, numPredictions);

    // Calculate confidence intervals if requested
    let intervals;
    if (this.config.includeConfidenceInterval) {
      intervals = this.calculatePredictionIntervals(
        values, 
        result.forecasts, 
        result.smoothed,
        this.config.confidenceLevel || 0.95
      );
    }

    // Format predictions
    const predictions = futureDates.map((date, index) => {
      const prediction = {
        x: formatDate(date, dateFormat || 'yyyy-MM-dd'),
        y: result.forecasts[index],
        _futureIndex: index,
        _isPrediction: true
      };

      if (intervals && intervals[index]) {
        prediction.confidenceLower = intervals[index].lower;
        prediction.confidenceUpper = intervals[index].upper;
      }

      return prediction;
    });

    // Format output
    const formattedPredictions = this.formatPredictionOutput(predictions, xAxis, yAxis, data[0]);

    return {
      predictions: formattedPredictions,
      metadata: {
        method: this.method,
        smoothingType: method,
        parameters: {
          alpha: this.alpha,
          beta: method !== 'simple' ? this.beta : null,
          gamma: method === 'triple' ? this.gamma : null,
          dampingFactor: result.damped ? this.dampingFactor : null
        },
        seasonalPeriod: method === 'triple' ? seasonalPeriod : null,
        dataPoints: timeSeries.length,
        isTimeSeries: true,
        frequency: frequency,
        averageInterval: averageInterval,
        confidenceLevel: this.config.confidenceLevel || 0.95,
        volatility: this.calculateCoefficientOfVariation(values),
        config: this.config
      }
    };
  }

  /**
   * Grid-search α/β/γ (step 0.1) on last 30% of series.
   */
  tuneParameters(values, method, seasonalPeriod) {
    const valSplit = Math.max(3, Math.floor(values.length * 0.3));
    const train = values.slice(0, -valSplit);
    const test = values.slice(-valSplit);
    const candidates = [0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9];
    let best = { err: Infinity, alpha: this.alpha, beta: this.beta, gamma: this.gamma };

    const mse = (a, b) => a.reduce((s, v, i) => s + Math.pow(v - b[i], 2), 0) / a.length;

    // Store original parameters
    const origAlpha = this.alpha;
    const origBeta = this.beta;
    const origGamma = this.gamma;

    for (const a of candidates) {
      for (const b of (method !== 'simple' ? candidates : [0])) {
        for (const g of (method === 'triple' ? candidates : [0])) {
          this.alpha = a; 
          this.beta = b; 
          this.gamma = g;
          
          try {
            const { forecasts } = method === 'simple' 
              ? this.simpleExponentialSmoothing(train, test.length)
              : method === 'double' 
              ? this.doubleExponentialSmoothing(train, test.length)
              : this.tripleExponentialSmoothing(train, test.length, seasonalPeriod || 12);
            
            const err = mse(test, forecasts.slice(0, test.length));
            if (err < best.err) {
              best = { err, alpha: a, beta: b, gamma: g };
            }
          } catch (e) {
            // Skip invalid parameter combinations
          }
        }
      }
    }

    // Restore best parameters
    this.alpha = best.alpha;
    this.beta = best.beta;
    this.gamma = best.gamma;

    return best;
  }
}

export default ExponentialSmoothingPredictor;